import type { Breadcrumb, PageParentHandlers } from '@nexus/ui';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Index of a history entry, set by the router on every entry it creates. */
function entryIndex(state: unknown): number | null {
  const index = (state as { __TSR_index?: unknown } | null)?.__TSR_index;
  return typeof index === 'number' ? index : null;
}

export interface PageBack extends PageParentHandlers {
  /** The level above the current page, from its breadcrumbs; `null` on top-level pages. */
  parent: Breadcrumb | null;
  /** Goes back to `parent`, e.g. after the swipe from the edge. */
  goBack: () => void;
}

/**
 * Way back of the current page (docs/ui/design-system.md, Zurück). The page header reports the
 * level above the page; going back returns in the history when the page before was that level,
 * so filters and scroll position of a list stay, and otherwise opens it.
 */
export function usePageBack(): PageBack {
  const navigate = useNavigate();
  const location = useRouterState({ select: (state) => state.location });
  const [parent, setParent] = useState<Breadcrumb | null>(null);
  // Path of each history entry the app has seen in this tab, by the router's index.
  const entries = useRef(new Map<number, string>());
  const index = entryIndex(location.state);

  useEffect(() => {
    if (index !== null) entries.current.set(index, location.pathname);
  }, [index, location.pathname]);

  const back = useCallback(
    (target: Breadcrumb) => {
      if (index === null || !target.href) return false;
      const previous = entries.current.get(index - 1);
      if (previous !== new URL(target.href, window.location.origin).pathname) return false;
      window.history.back();
      return true;
    },
    [index],
  );

  const goBack = useCallback(() => {
    if (!parent?.href || back(parent)) return;
    const url = new URL(parent.href, window.location.origin);
    void navigate({ to: url.pathname, search: Object.fromEntries(url.searchParams) });
  }, [back, navigate, parent]);

  return useMemo(() => ({ parent, report: setParent, back, goBack }), [parent, back, goBack]);
}
